import { Button } from "@/components/ui/button";
import { Users, Medal, PenLine, Mail, Rocket, Bot, Camera } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const VolunteerEngagementPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO 
        title="AI-Powered Volunteer Engagement Tools for Nonprofits" 
        description="Streamline volunteer recruitment, onboarding, and recognition to build a thriving volunteer community with our AI-powered tools."
      />
      <div className="min-h-screen bg-gradient-to-b from-[#F2FCE2] to-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-[#2D7D46] text-white py-20">
          <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10" />
          <div className="container mx-auto px-4 relative">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Volunteer Engagement & Growth
              </h1>
              <p className="text-xl md:text-2xl text-gray-100 mb-1">
                Recruit, Engage, and Retain Volunteers with AI-Driven Tools
              </p>
            </div>
          </div>
        </div>

        {/* Challenges Section */}
        <div className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                The Challenges Nonprofits Face with Volunteer Engagement
              </h2>
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-[#2D7D46] mb-4">
                    <Users className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Drop-off in Engagement</h3>
                  <p className="text-gray-600">
                    Nearly 40% of new volunteers don't return after their first event due to insufficient follow-up.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-[#2D7D46] mb-4">
                    <PenLine className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Manual Processes</h3>
                  <p className="text-gray-600">
                    Managing applications, communication, and scheduling takes hours of staff time every week.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-[#2D7D46] mb-4">
                    <Medal className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Recognition Gaps</h3>
                  <p className="text-gray-600">
                    Volunteers who feel unappreciated are less likely to remain engaged long-term.
                  </p>
                </div>
              </div>
              <div className="text-center bg-[#2D7D46]/5 rounded-xl p-8">
                <p className="text-lg text-gray-700 italic">
                  "Organizations that implement volunteer recognition programs report a 35% increase in retention rates."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Solutions Section */}
        <div className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              AI-Powered Volunteer Engagement Tools
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="p-3 rounded-lg bg-[#2D7D46]/10 text-[#2D7D46] w-fit mb-4">
                  <Mail className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Recruitment Emails</h3>
                <p className="text-gray-600 mb-4">
                  Personalized volunteer recruitment emails that clearly communicate opportunities and impact.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Automated Reminders</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Personalized content</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Calendar Blocks</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="p-3 rounded-lg bg-[#2D7D46]/10 text-[#2D7D46] w-fit mb-4">
                  <Camera className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Recognition Content</h3>
                <p className="text-gray-600 mb-4">
                  AI-generated social posts and digital certificates to celebrate volunteer contributions.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Custom Certificates</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Spotlight stories</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Event Photos</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="p-3 rounded-lg bg-[#2D7D46]/10 text-[#2D7D46] w-fit mb-4">
                  <Bot className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">AI Volunteer Chatbot</h3>
                <p className="text-gray-600 mb-4">
                  Handle registrations, FAQs, and event reminders automatically to create a seamless experience.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>24/7 support</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Event level information</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Event reminders</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Impact Stats Section */}
        <div className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              The Impact of AI on Volunteer Engagement
            </h2>
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">35%</div>
                <p className="text-gray-600">Higher retention rates</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">50%</div>
                <p className="text-gray-600">More sign-ups</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">80%</div>
                <p className="text-gray-600">Less admin work</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">60%</div>
                <p className="text-gray-600">Higher engagement</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-20 bg-[#2D7D46] text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Get Started with Smart Volunteer Engagement
            </h2>
            <p className="text-xl mb-12 max-w-2xl mx-auto">
              Join hundreds of nonprofits using AI to recruit, retain, and recognize volunteers more effectively.
            </p>
            <div className="space-y-4">
              <Button 
                size="lg"
                className="bg-white text-[#2D7D46] hover:bg-gray-100 text-lg px-8 py-6 h-auto w-full md:w-auto"
                onClick={() => navigate("/signup")}
              >
                <Rocket className="w-5 h-5 mr-2" />
                Start Your First Volunteer Outreach
              </Button>
              <div className="mt-4">
                <Link 
                  to="/npo/tools/volunteer" 
                  className="text-white hover:underline inline-flex items-center"
                >
                  Explore More Volunteer Tools →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VolunteerEngagementPage;
